import { Car, Coins, PhoneCall, ShieldCheck, ShoppingBag, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, Card } from "react-daisyui";
import { useNavigate } from "react-router-dom";

import { fetchHeroSection } from "@/services/homeService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function Hero() {
  const navigate = useNavigate();

  // Hero content state
  const [heroData, setHeroData] = useState<null | {
    buttons: { label: string; link: string; }[];
    image: string;
    subtitle: string;
    title: string;
  }>(null);
  const [loading, setLoading] = useState(true);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchHeroSection().then((section) => {
      if (section?.content) {
        setHeroData(section.content);
      }
      setLoading(false);
    });
    fetchHeroBackgrounds("hero").then((data) => {
          setHeroBackground(data);
          setHeroLoading(false);
        });
  }, []);

return (
  /* 1. Added a relative container to bound the absolute background inside the Hero */
  <div className="relative w-full overflow-hidden">
    
    {/* 2. Changed "fixed" to "absolute" so it scrolls away naturally with the hero section */}
    <div className="absolute top-0 left-0 right-0 z-0 pointer-events-none w-full" style={{ height: "600px" }}>
      <div className="absolute inset-0 opacity-30">
        {loading ? (
          <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-info/10 to-primary/10">
            <div
              className="animate-shimmer absolute inset-0 bg-gradient-to-r from-info/10 via-primary/10 to-info/10"
              style={{ backgroundSize: "200% 100%" }}
            />
          </div>
        ) : heroBackground?.imageUrl ? (
            <img
              alt={heroBackground?.imageAlt || "Hero background"}
              className="h-full w-full object-cover"
              src={heroBackground.imageUrl}
              style={{ display: heroBackground?.imageUrl ? "block" : "none" }}
            />
          ) : (
          <div className="h-full w-full bg-gradient-to-br from-info/5 to-primary/5" />
        )}
      </div>
    </div>

    {/* The main section content remains unchanged but now overlays correctly */}
    <section className="relative flex flex-col items-center justify-center bg-transparent px-4 pb-12 pt-24 sm:pt-32 md:pb-24 z-10">
      {/* Hero Content */}
      <div className="max-w-2xl text-center">
        {loading ? (
          <>
            <ModernShimmer
              className="mx-auto mb-4 sm:h-16 md:h-20"
              height="4rem"
              width="75%"
            />
            <ModernShimmer
              className="mx-auto mb-8 sm:h-8"
              height="2rem"
              width="66%"
            />
          </>
        ) : (
          <>
            <h1 className="mb-4 text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl">
              {heroData?.title} <br />
              <span className="text-primary">All in One Place</span>
            </h1>
            <p className="mb-8 text-base text-base-content/70 sm:text-lg md:text-xl">
              {heroData?.subtitle}
            </p>
          </>
        )}
        <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
          {heroData?.buttons && heroData.buttons.length > 0 ? (
            heroData.buttons.map((button, index) => (
              <Button
                className="font-semibold"
                color={index === 0 ? "neutral" : "primary"}
                key={index}
                onClick={() => navigate(button.link)}
                size="lg"
              >
                {button.label}
              </Button>
            ))
          ) : (
            <>
              <Button
                className="font-semibold"
                color="neutral"
                onClick={() => navigate("/contact-us")}
                size="lg"
              >
                Learn More
              </Button>
              <Button
                className="font-semibold"
                color="primary"
                onClick={() => navigate("/pages/about-us/updates")}
                size="lg"
              >
                Be Updated
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Feature Cards */}
      <div className="w-full max-w-6xl px-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FeatureCard
            desc="Every service provider undergoes strict criminal and background verification before joining our team."
            icon={<ShieldCheck className="h-8 w-8 text-primary" />}
            title="Background Checked"
          />
          <FeatureCard
            desc="Not happy with the results? We will send a team back to make it right at no extra cost to you."
            icon={<Sparkles className="h-8 w-8 text-secondary" />}
            title="Satisfaction Guaranteed"
          />
          <FeatureCard
            desc="All bookings are fully insured against accidental damages, ensuring complete peace of mind."
            icon={<Coins className="h-8 w-8 text-green-500" />}
            title="Insured Services"
          />
          <FeatureCard
            desc="Always here to assist you with your cleaning, laundry, or carwash bookings."
            icon={<PhoneCall className="h-8 w-8 text-blue-500" />}
            title="24/7 Support"
          />
        </div>
      </div>
    </section>
  </div>
);

}

// Add shimmer animation to global styles if not present
if (
  typeof document !== "undefined" &&
  !document.getElementById("modern-shimmer-style")
) {
  const style = document.createElement("style");
  style.id = "modern-shimmer-style";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .animate-shimmer {
      animation: shimmer 1.5s linear infinite;
    }
  `;
  document.head.appendChild(style);
}

function FeatureCard({
  desc,
  icon,
  title,
}: {
  desc: string;
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Card className="flex flex-col items-center rounded-xl border border-base-200 bg-base-100 p-6 text-center shadow-md">
      <div className="mb-3">{icon}</div>
      <Card.Title className="mb-1 text-lg font-bold" tag="h3">
        {title}
      </Card.Title>
      <p className="text-sm text-base-content/70">{desc}</p>
    </Card>
  );
}

// Modern shimmer component (move outside Hero)
function ModernShimmer({
  className = "",
  height,
  width,
}: {
  className?: string;
  height: string;
  width: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
      style={{ height, width }}
    >
      <div
        className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
        style={{ backgroundSize: "200% 100%" }}
      />
    </div>
  );
}
